import { ButtonClose, ButtonIcon } from 'front-end/src/components'
import * as _ from 'lodash'
import React, { useContext } from 'react'
import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc'
import { Trans, t } from '@lingui/macro'

import {
  ISurveyQuestionOption,
  SurveyQuestionTypes
} from '../../../../../../shared/surveys'
import { CheckboxBig } from '../../../../components/common/form/checkbox/CheckboxBig'
import { FormRow } from '../../../../components/common/form/Form'
import Input from '../../../../components/common/form/input/Input'
import IconTick from '../../../../components/common/icon/IconTick'
import IconX from '../../../../components/common/icon/IconX'
import { notification } from '../../../../components/common/notifications/Notifications'
import { isSurveyEditable } from '../../helpers'
import { ISurveyQuestion } from '../../types'

import { css } from '@emotion/react'

import './SurveyQuestionOptions.less'
import { brandColors } from 'shared/basicPalette'
import { ManageSurveyContext } from '../ManageSurveyContext'

interface ISurveyQuestionOptionsProps {
  allowDelete: boolean
  question: ISurveyQuestion
  updateOptions: (options: ISurveyQuestionOption[]) => void
}

export const SurveyQuestionOptions = ({
  allowDelete = true,
  question,
  updateOptions
}: ISurveyQuestionOptionsProps) => {
  const type = question.type
  const options = question.content?.options!

  const onSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) {
      return null
    }

    const newOptionsOrder = [...options]
    newOptionsOrder.splice(newIndex, 0, newOptionsOrder.splice(oldIndex, 1)[0])
    await updateOptions(newOptionsOrder)
  }

  return (
    <>
      {type === SurveyQuestionTypes.RATING && (
        <div className="SurveyQuestionOption__label">
          <Trans>Highest</Trans>
        </div>
      )}
      <SortableDiv
        className="SurveyQuestion__options"
        lockAxis="y"
        useDragHandle={true}
        onSortEnd={onSortEnd}
      >
        {options.map((option, index) => (
          <OptionItemElement
            key={`${question.id}-${option.id}`}
            index={index}
            question={question}
            option={option}
            allowDelete={allowDelete}
            updateOptions={updateOptions}
          />
        ))}
      </SortableDiv>
      {type === SurveyQuestionTypes.RATING && (
        <div className="SurveyQuestionOption__label">
          <Trans>Lowest</Trans>
        </div>
      )}
    </>
  )
}

const OptionItemElement = SortableElement(
  ({
    option,
    question,
    updateOptions,
    allowDelete
  }: {
    option: ISurveyQuestionOption
    question: ISurveyQuestion
    updateOptions: (options: ISurveyQuestionOption[]) => void
    allowDelete: boolean
  }) => {
    const { survey } = useContext(ManageSurveyContext)
    const { type, content } = question
    const options = content?.options!

    const index = options.indexOf(option)
    const iconButtonStyle = {
      color: brandColors.grey.mid,
      minW: '32px',
      h: '32px',
      bg: 'none',
      _hover: {
        bg: brandColors.grey.lighter
      }
    }
    return (
      <FormRow className={`SurveyQuestionOption SurveyQuestionOption--${type}`}>
        {type === SurveyQuestionTypes.RATING && (
          <div
            className={`SurveyQuestionOption__ratingDot SurveyQuestionOption__ratingDot--${Math.abs(
              index - 10
            )}`}
          />
        )}

        {type === SurveyQuestionTypes.BOOL &&
          (index === 0 ? <IconX /> : <IconTick />)}

        {type === SurveyQuestionTypes.CHOOSE_ONE && (
          <CheckboxBig isRounded noIcon mr="16px" isChecked={false} />
        )}
        {type === SurveyQuestionTypes.MULTIPLE_CHOICE && (
          <CheckboxBig mr="16px" isChecked={false} />
        )}

        <Input
          disabled={!isSurveyEditable(survey)}
          defaultValue={option.label}
          title={t`Option title`}
          placeholder={t`Option title comes here`}
          onChange={async (value: string) => {
            const newOptions: ISurveyQuestionOption[] = _.cloneDeep(options)
            const changedOption = newOptions.find((o) => o.id === option.id)
            if (changedOption) {
              changedOption.label = value
              await updateOptions(newOptions)
            }
          }}
        />
        {allowDelete && isSurveyEditable(survey) && (
          <aside className="SurveyQuestion__actions">
            <ButtonClose
              disabled={!isSurveyEditable(survey)}
              onClick={async () => {
                if (options.length < 3) {
                  const minValue = 2
                  notification.warning(t`Minimum is ${minValue} options`)
                  return null
                }

                await updateOptions(_.reject(options, { id: option.id }))
              }}
            />
            <ButtonIcon
              {...iconButtonStyle}
              aria-label={t`Drag`}
              css={css`
                .icon-menu {
                  width: 100%;
                  line-height: 32px;
                }
              `}
            >
              <DragHandle />
            </ButtonIcon>
          </aside>
        )}
      </FormRow>
    )
  }
)

const SortableDiv = SortableContainer((props) => <div {...props} />)
const DragHandle = SortableHandle(() => <i className="Icon icon-menu" />)
